import './ActivityFeed.scss';
import { ReactNode, useEffect } from 'react';
import { AnyActivity } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Page } from '@Components/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useSetError } from '../../redux/modals';
import { ActivityFeedCidSelect } from './ActivityFeedCidSelect';
import { ActivityFeedDetail } from './Detail/ActivityFeedDetail';
import { ActivityFeedList } from './List/ActivityFeedList';
import { ActivityFeedDataProvider, useActivityFeedData } from './useActivityFeedData';
import { MultiSelectProvider } from './useActivityFeedMultiSelect';
import { SelectionProvider } from './useActivityFeedSelection';
import { OracleCidScopeProvider } from './useOracleCidScope';
import { useOracleEventsData } from './useOracleEventsData';
import { ActivityFeedFilters } from './Filters/ActivityFeedFilters';

export const ActivityFeed = (): ReactNode => (
  <OracleCidScopeProvider>
    <ActivityFeedPage />
  </OracleCidScopeProvider>
);

const ActivityFeedPage = (): ReactNode => {
  const {
    activities,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    requiresCidSelection
  } = useOracleEventsData();
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load activity feed",
        message: error.message,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <Page
      title='Activity Feed'
      pageType='Activity Feed'
      isLoading={isLoading}
      className='activity-feed-page'
    >
      <ActivityFeedBody
        activities={activities}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        requiresCidSelection={requiresCidSelection}
      />
    </Page>
  );
};

interface ActivityFeedBodyProps {
  activities: AnyActivity[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  requiresCidSelection: boolean;
}

// Page.isLoading is telemetry-only — the loading UI happens here so the provider mounts
// only once we have data.
const ActivityFeedBody = ({
  activities,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  requiresCidSelection
}: ActivityFeedBodyProps): ReactNode => {
  if (isLoading) return <FullPageSpinner />;
  if (requiresCidSelection) return <ActivityFeedCidSelect />;
  return (
    <ActivityFeedDataProvider
      rawActivities={activities}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
    >
      <ActivityFeedLayout />
    </ActivityFeedDataProvider>
  );
};

const ActivityFeedLayout = (): ReactNode => {
  const { activityIds, activitiesById } = useActivityFeedData();
  return (
    <SelectionProvider activityIds={activityIds} activitiesById={activitiesById}>
      <MultiSelectProvider activityIds={activityIds}>
        <div className='activity-feed-layout'>
          <ActivityFeedFilters />
          <ActivityFeedList />
          <ActivityFeedDetail />
        </div>
      </MultiSelectProvider>
    </SelectionProvider>
  );
};
